import { UseFormReturn } from '@graphcommerce/ecommerce-ui'
import { filterNonNullableKeys } from '@graphcommerce/next-ui'
import { MenuItem } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { StandardDropdown } from '../../Dropdown/StandardDropdown'
import { OptionTypeRenderer } from '../../Product/ProductCustomizable/ProductCustomizable'

export const ColorspecDropDownOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
    quantities: Record<number, number>
    fouroverOptionModifier?: string
  }
>((props) => {
  const {
    uid,
    required,
    optionIndex,
    index,
    title,
    dropdownValue,
    form,
    quantities,
    fouroverOptionModifier,
  } = props
  const { register, setValue, getFieldState } = form

  const [color, setColor] = useState<null | string>()
  const { watch } = form

  const quantity = watch('quantity')

  const enteredOptions = watch('enteredOptions')

  const uniqueColorOptions = useMemo(() => {
    const uniqueTitles: string[] = []
    if (!dropdownValue) {
      return []
    }
    return dropdownValue
      .filter((v) => v && v.price)
      .map((v) => {
        const valueParts = (v?.title || '').split(' - ')
        if (valueParts.length > 1) {
          return {
            ...v,
            title: valueParts[0],
          }
        }
        return v
      })
      .filter((v) => {
        if (!v || !v.title || uniqueTitles.includes(v?.title)) {
          return false
        }
        uniqueTitles.push(v.title)
        return true
      })
  }, [dropdownValue])

  const setOptionByColorAndQty = useCallback(
    (colorOptionId, newQuantity) => {
      let qty = newQuantity || Object.keys(quantities).at(0)
      if (qty) {
        const colorTitle = uniqueColorOptions.find(
          (c) => c?.option_type_id?.toString() === colorOptionId,
        )?.title
        const colorOptionFullTitle = `${colorTitle} - ${qty}`
        let selectedRealOption = dropdownValue?.find((v) => v?.title === colorOptionFullTitle)
        if (!selectedRealOption) {
          // Sometimes when simple is changed, the previous qty is not available for new one.
          // So we will just use the first qty available
          qty = Object.keys(quantities).at(0)
          const colorOptionFullTitleFirst = `${colorTitle} - ${qty}`
          selectedRealOption = dropdownValue?.find((v) => v?.title === colorOptionFullTitleFirst)
        }
        const selectedOptionId = selectedRealOption?.uid
        if (selectedOptionId) {
          const entered = Array.isArray(enteredOptions)
            ? [...enteredOptions].filter((item) => item)
            : []
          if (entered.find((o) => o.uid === uid)) {
            entered.find((o) => o.uid === uid).value = selectedOptionId
          } else {
            entered.push({ uid, value: selectedOptionId })
          }
          setValue(`enteredOptions`, entered, {})
          setValue(`quantity`, qty)
        }
      }
    },
    [uniqueColorOptions, dropdownValue, enteredOptions, setValue, uid, quantities],
  )

  // DO NOT ADD setOptionByColorAndQty as dependency to avoid loop
  useEffect(() => {
    setOptionByColorAndQty(color, quantity)
  }, [quantity, color])

  useEffect(() => {
    // Autoselect the first color
    if (uniqueColorOptions && uniqueColorOptions.length > 0) {
      setColor(uniqueColorOptions.at(0)?.option_type_id?.toString())
    }
  }, [uniqueColorOptions])

  if (
    !dropdownValue ||
    (fouroverOptionModifier === 'DisableIfSingle' && dropdownValue?.length < 2) ||
    fouroverOptionModifier === 'Disable'
  ) {
    return null
  }

  return (
    <StandardDropdown
      value={color || ''}
      label={title || ''}
      onChange={(newValue) => {
        if (uid && newValue?.target?.value) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setColor(newValue?.target?.value?.toString())
        }
      }}
      options={filterNonNullableKeys(uniqueColorOptions, ['title']).map((option) => (
        <MenuItem
          value={option.option_type_id?.toString() || ''}
          className='flex w-full items-center justify-between'
        >
          <span>{option.title}</span>
        </MenuItem>
      ))}
      twoColumns
      fullWidth
      placeholder='Select...'
    />
  )
})
